import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Divider,
} from "@mui/material";
import { FormattedMessage } from "react-intl";
import { v4 as uuidv4 } from "uuid";
import { PlusOutlined } from "@ant-design/icons";
import { useTheme } from "@mui/material/styles";

// project import
import MainCard from "components/MainCard";
import { SolidButton } from "components/buttons/SolidButton";
import { OutlineButton } from "components/buttons/OutlineButton";
import CheckboxField from "components/CheckboxField";
import { ReactComponent as BinIcon } from "assets/images/icons/icn_table_cell_Bin.svg";
import { ReactComponent as BinHoverIcon } from "assets/images/icons/icn_table_cell_Bin_hover.svg";

// utils
import localStorageUtil from "utils/localStorageUtil";

// Hooks
import useAuthScope from "hooks/useAuthScope";
import useDataSourceManager from "hooks/useDataSourceManager";

// ==============================|| COMPONENTS ||============================== ||

const TrashIconButton = ({ onClick, size = "16px", sx = {} }) => {
  const [isHovered, setIsHovered] = React.useState(false);
  return (
    <IconButton
      size='small'
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
      sx={{ p: 0.5, ...sx }}
    >
      {isHovered ? (
        <BinHoverIcon style={{ width: size, height: size }} />
      ) : (
        <BinIcon style={{ width: size, height: size }} />
      )}
    </IconButton>
  );
};

// ==============================|| CONSTANTS ||============================== ||

const getDefaultOrgs = () => [
  {
    id: uuidv4(),
    name: "TechCorp HQ",
    aiChatbotEnabled: false,
    mspEnabled: false,
    sites: [
      {
        id: uuidv4(),
        name: "HQ Main",
        accessPoints: 20,
        switches: 10,
        nmsPro: false,
        healthAi: false,
      },
    ],
  },
];

// ==============================|| CALCULATION LOGIC ||============================== ||

const calculateSiteDailyBurn = (site, mspEnabled) => {
  const deviceCount =
    (Number(site.accessPoints) || 0) + (Number(site.switches) || 0);
  const baseRate = mspEnabled ? 15 : 0;
  const nmsRate = site.nmsPro ? 1 : 0;
  const healthAiRate = site.healthAi ? 1 : 0;

  // 使用 Math.max 避免 NMS Pro 與 Health AInsight 點數重複累加，確保啟用 AI 時每台設備為 1 點
  return deviceCount * (baseRate + Math.max(nmsRate, healthAiRate));
};

const calculateOrgDailyBurn = (org) => {
  const chatbotRate = org.aiChatbotEnabled ? 1 : 0;
  const sitesBurn = org.sites.reduce(
    (acc, site) => acc + calculateSiteDailyBurn(site, org.mspEnabled),
    0,
  );
  return chatbotRate + sitesBurn;
};

const STORAGE_KEY = "usage_emulator_config";

// ==============================|| USAGE EMULATOR PAGE ||============================== ||

const UsageEmulator = () => {
  const theme = useTheme();

  const [orgs, setOrgs] = useState([]);
  const { auth, standalone, isAuthReady } = useAuthScope();
  const { sendBatch } = useDataSourceManager();
  const [currentBalance, setCurrentBalance] = useState(0);
  const [isBalanceLoading, setIsBalanceLoading] = useState(false);

  // Derived calculations
  const totalDailyBurn = useMemo(() => {
    return orgs.reduce((acc, org) => acc + calculateOrgDailyBurn(org), 0);
  }, [orgs]);

  // 計算預估可用天數 (Est. Lifetime = 餘額 / 每日消耗)
  const estimatedLifetime = useMemo(() => {
    if (totalDailyBurn === 0) return "∞";
    const days = Math.floor(currentBalance / totalDailyBurn);
    return days < 0 ? 0 : days;
  }, [currentBalance, totalDailyBurn]);

  const dashboardUuid = useMemo(() => uuidv4(), []);

  const getIds = () => ({
    realmId: auth.realm?.id || "",
    orgId: auth.orgs?.id || "",
  });

  const fetchLicenseDashboard = () => {
    const { realmId, orgId } = getIds();
    const dashboardKey = standalone
      ? "/orgs/:orgId/license/dashboard.read"
      : "/ents/:entId/orgs/:orgId/license/dashboard.read";
    const restfulParams = standalone
      ? { orgId: realmId }
      : { entId: realmId, orgId };

    return {
      apiKey: dashboardKey,
      restfulPlaceholders: restfulParams,
      params: {},
      requestUuid: dashboardUuid,
    };
  };

  const textfieldStyle = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "20px",
      height: "34px",
      "& fieldset": {
        borderColor: "#172664",
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
    "& .MuiOutlinedInput-input": {
      fontSize: "13px",
      color: "#172664",
      py: 0,
    },
  };

  // 載入時取得當前點數餘額 (Current Balance)
  useEffect(() => {
    const getBalance = async () => {
      if (!isAuthReady) return;
      setIsBalanceLoading(true);
      try {
        const { getSendBatchData } = await sendBatch([fetchLicenseDashboard()]);
        const dashboardRes = getSendBatchData({ requestUuid: dashboardUuid });
        if (dashboardRes?.data?.balance !== undefined) {
          setCurrentBalance(Number(dashboardRes.data.balance) || 0);
        }
      } catch (error) {
        console.error("[UsageEmulator] Error fetching balance:", error);
      } finally {
        setIsBalanceLoading(false);
      }
    };
    getBalance();
  }, [isAuthReady, auth.orgs?.id, auth.realm?.id]);

  // Load from local storage on mount
  useEffect(() => {
    const saved = localStorageUtil.getItem(STORAGE_KEY);
    if (saved && Array.isArray(saved)) {
      setOrgs(saved);
    } else {
      setOrgs(getDefaultOrgs());
    }
  }, []);

  // Handlers
  const handleSave = () => {
    localStorageUtil.setItem(STORAGE_KEY, orgs);
  };

  const handleAddOrg = () => {
    setOrgs([
      ...orgs,
      {
        id: uuidv4(),
        name: "New Organization",
        aiChatbotEnabled: false,
        mspEnabled: false,
        sites: [
          {
            id: uuidv4(),
            name: "New Site",
            accessPoints: 0,
            switches: 0,
            nmsPro: false,
            healthAi: false,
          },
        ],
      },
    ]);
  };

  const handleDeleteOrg = (orgId) => {
    setOrgs(orgs.filter((o) => o.id !== orgId));
  };

  const handleUpdateOrg = (orgId, fields) => {
    setOrgs(orgs.map((o) => (o.id === orgId ? { ...o, ...fields } : o)));
  };

  const handleAddSite = (orgId) => {
    setOrgs(
      orgs.map((o) => {
        if (o.id === orgId) {
          return {
            ...o,
            sites: [
              ...o.sites,
              {
                id: uuidv4(),
                name: "New Site",
                accessPoints: 0,
                switches: 0,
                nmsPro: false,
                healthAi: false,
              },
            ],
          };
        }
        return o;
      }),
    );
  };

  const handleDeleteSite = (orgId, siteId) => {
    setOrgs(
      orgs.map((o) => {
        if (o.id === orgId) {
          return {
            ...o,
            sites: o.sites.filter((s) => s.id !== siteId),
          };
        }
        return o;
      }),
    );
  };

  const handleUpdateSite = (orgId, siteId, fields) => {
    setOrgs(
      orgs.map((o) => {
        if (o.id === orgId) {
          return {
            ...o,
            sites: o.sites.map((s) =>
              s.id === siteId ? { ...s, ...fields } : s,
            ),
          };
        }
        return o;
      }),
    );
  };

  const handleReset = () => {
    setOrgs(getDefaultOrgs());
  };

  const handleExport = () => {
    // Remove aiChatbotEnabled and mspEnabled keys before export
    const exportData = orgs.map((org) => {
      // eslint-disable-next-line no-unused-vars
      const { aiChatbotEnabled, mspEnabled, ...rest } = org;
      return rest;
    });

    const dataStr =
      "data:text/json;charset=utf-8," +
      encodeURIComponent(JSON.stringify(exportData, null, 2));
    const downloadAnchorNode = document.createElement("a");
    downloadAnchorNode.setAttribute("href", dataStr);
    downloadAnchorNode.setAttribute("download", "usage_emulator_config.json");
    document.body.appendChild(downloadAnchorNode);
    downloadAnchorNode.click();
    downloadAnchorNode.remove();
  };

  const handleImport = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const json = JSON.parse(e.target.result);
          setOrgs(json);
        } catch (err) {
          alert("Invalid JSON file");
        }
      };
      reader.readAsText(file);
    }
  };

  return (
    <Box>
      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='center'
        sx={{
          mb: 3,
          height: "64px",
          mt: -1,
        }}
      >
        <Typography
          variant='h4'
          sx={{
            color: "#172664",
            fontFamily: theme.typography.fontFamily,
            fontSize: "22px",
            fontWeight: "bold",
          }}
        >
          <FormattedMessage id='usage-emulator' />
        </Typography>

        <Stack
          direction='row'
          alignItems='center'
          spacing={2}
          sx={{ height: "100%" }}
        >
          <Divider
            orientation='vertical'
            sx={{
              height: "40px",
              borderColor: "rgba(0,0,0,0.08)",
              borderRightWidth: "1px",
              mx: 1,
            }}
          />
          <OutlineButton onClick={handleReset} height='34px'>
            Reset
          </OutlineButton>
          <OutlineButton onClick={handleExport} height='34px'>
            Export
          </OutlineButton>
          <OutlineButton component='label' height='34px'>
            Import
            <input type='file' hidden accept='.json' onChange={handleImport} />
          </OutlineButton>
          <SolidButton onClick={handleSave} height='34px'>
            Save
          </SolidButton>
        </Stack>
      </Stack>

      <Grid container spacing={4}>
        {/* Simulated Result Section */}
        <Grid item xs={12}>
          <MainCard
            content={false}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: "20px",
            }}
          >
            <Box sx={{ p: 4 }}>
              {/* 使用 spacing={8} 並加上 flex 包裝，確保小螢幕也能完美排列 */}
              <Stack direction='row' spacing={8} useFlexGap flexWrap='wrap'>
                {/* 1. Current Balance 卡片 */}
                <Box>
                  <Typography
                    sx={{
                      color: "#172664",
                      fontWeight: "bold",
                      fontSize: "15px",
                      mb: 1,
                    }}
                  >
                    Current Balance
                  </Typography>
                  <Stack spacing={0.5}>
                    <Typography
                      sx={{
                        fontSize: "42px",
                        fontWeight: "bold",
                        color: "#008514",
                        lineHeight: 1,
                      }}
                    >
                      {isBalanceLoading
                        ? "-"
                        : currentBalance.toLocaleString() || 0}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "#373737",
                        fontWeight: "bold",
                      }}
                    >
                      Credits
                    </Typography>
                  </Stack>
                </Box>

                {/* 2. Daily Usage 卡片 */}
                <Box>
                  <Typography
                    sx={{
                      color: "#172664",
                      fontWeight: "bold",
                      fontSize: "15px",
                      mb: 1,
                    }}
                  >
                    Daily Usage
                  </Typography>
                  <Stack spacing={0.5}>
                    <Typography
                      sx={{
                        fontSize: "42px",
                        fontWeight: "bold",
                        color: "#008514",
                        lineHeight: 1,
                      }}
                    >
                      {totalDailyBurn.toLocaleString() || 0}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "#373737",
                        fontWeight: "bold",
                      }}
                    >
                      Credits
                    </Typography>
                  </Stack>
                </Box>

                {/* 3. Month Usage 卡片 */}
                <Box>
                  <Typography
                    sx={{
                      color: "#172664",
                      fontWeight: "bold",
                      fontSize: "15px",
                      mb: 1,
                    }}
                  >
                    Month Usage (30 Days)
                  </Typography>
                  <Stack spacing={0.5}>
                    <Typography
                      sx={{
                        fontSize: "42px",
                        fontWeight: "bold",
                        color: "#008514",
                        lineHeight: 1,
                      }}
                    >
                      {(totalDailyBurn * 30).toLocaleString() || 0}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "#373737",
                        fontWeight: "bold",
                      }}
                    >
                      Credits
                    </Typography>
                  </Stack>
                </Box>

                {/* 5. Year Usage 卡片 */}
                <Box>
                  <Typography
                    sx={{
                      color: "#172664",
                      fontWeight: "bold",
                      fontSize: "15px",
                      mb: 1,
                    }}
                  >
                    Year Usage (365 Days)
                  </Typography>
                  <Stack spacing={0.5}>
                    <Typography
                      sx={{
                        fontSize: "42px",
                        fontWeight: "bold",
                        color: "#008514",
                        lineHeight: 1,
                      }}
                    >
                      {(totalDailyBurn * 365).toLocaleString() || 0}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "#373737",
                        fontWeight: "bold",
                      }}
                    >
                      Credits
                    </Typography>
                  </Stack>
                </Box>
              </Stack>
            </Box>
          </MainCard>
        </Grid>

        {/* Configuration Section */}
        <Grid item xs={12}>
          <Stack
            direction='row'
            justifyContent='space-between'
            alignItems='center'
            sx={{ mb: 2 }}
          >
            <Stack spacing={0.5}>
              <Typography
                sx={{
                  color: "#172664",
                  fontSize: "18px",
                  fontWeight: "bold",
                }}
              >
                Configuration
              </Typography>
              <Typography sx={{ fontSize: "14px", color: "text.secondary" }}>
                Edits below update the simulated results above.
              </Typography>
            </Stack>
          </Stack>

          <Stack spacing={4}>
            {orgs.map((org) => (
              <MainCard
                key={org.id}
                content={false}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: "20px",
                }}
              >
                <Box sx={{ p: 4 }}>
                  <Stack spacing={3}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "18px",
                          fontWeight: "bold",
                          color: "#172664",
                        }}
                      >
                        {org.name}
                      </Typography>
                      <Stack direction='row' spacing={2} alignItems='center'>
                        <OutlineButton
                          size='small'
                          startIcon={
                            <PlusOutlined
                              style={{
                                border: "1.5px solid",
                                borderRadius: "50%",
                                padding: "1px",
                                fontSize: "10px",
                              }}
                            />
                          }
                          onClick={() => handleAddSite(org.id)}
                          width='auto'
                          height='30px'
                          sx={{
                            borderRadius: "8px",
                            color: "#172664",
                            borderColor: "#172664",
                          }}
                        >
                          Add Site
                        </OutlineButton>
                        <TrashIconButton
                          onClick={() => handleDeleteOrg(org.id)}
                          size='20px'
                        />
                      </Stack>
                    </Box>

                    <Box sx={{ display: "none", gap: 4 }}>
                      <CheckboxField
                        label='AI Chatbot: Enable'
                        checked={org.aiChatbotEnabled}
                        onChange={(checked) =>
                          handleUpdateOrg(org.id, {
                            aiChatbotEnabled: checked,
                          })
                        }
                      />
                      <CheckboxField
                        label='MSP: Enable'
                        checked={org.mspEnabled}
                        onChange={(checked) =>
                          handleUpdateOrg(org.id, {
                            mspEnabled: checked,
                          })
                        }
                      />
                    </Box>

                    <TableContainer>
                      <Table size='small'>
                        <TableHead>
                          {/* 透過 CSS 選擇器，讓此 Row 下的所有標題單元格 (th) 統一向頂部對齊，達成完美的第一行水平對齊 */}
                          <TableRow sx={{ "& th": { verticalAlign: "top" } }}>
                            <TableCell
                              sx={{
                                fontSize: "14px",
                                fontWeight: "bold",
                                color: "#172664",
                                borderBottom: `1px solid ${theme.palette.divider}`,
                                pb: 2,
                                width: "30%",
                              }}
                            >
                              Site
                            </TableCell>
                            <TableCell
                              align='center'
                              sx={{
                                fontSize: "14px",
                                fontWeight: "bold",
                                color: "#172664",
                                borderBottom: `1px solid ${theme.palette.divider}`,
                                pb: 2,
                              }}
                            >
                              Access Points
                            </TableCell>
                            <TableCell
                              align='center'
                              sx={{
                                fontSize: "14px",
                                fontWeight: "bold",
                                color: "#172664",
                                borderBottom: `1px solid ${theme.palette.divider}`,
                                pb: 2,
                              }}
                            >
                              Switches
                            </TableCell>
                            <TableCell
                              align='center'
                              sx={{
                                fontSize: "14px",
                                fontWeight: "bold",
                                color: "#172664",
                                borderBottom: `1px solid ${theme.palette.divider}`,
                                pb: 2,
                              }}
                            >
                              <Stack spacing={0.5} alignItems='center'>
                                <Box>Health AI Insight</Box>
                                <Typography
                                  sx={{
                                    fontSize: "11px",
                                    color: "text.secondary",
                                    fontWeight: "normal",
                                  }}
                                >
                                  1 credit/day/device
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell
                              align='right'
                              sx={{
                                fontSize: "14px",
                                fontWeight: "bold",
                                color: "#172664",
                                borderBottom: `1px solid ${theme.palette.divider}`,
                                pb: 2,
                              }}
                            >
                              Action
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {org.sites.map((site, index) => (
                            <TableRow
                              key={site.id}
                              sx={{
                                bgcolor:
                                  index % 2 === 1 ? "grey.50" : "transparent",
                                "& td": { borderBottom: "none", py: 1.5 },
                              }}
                            >
                              <TableCell>
                                <TextField
                                  size='small'
                                  fullWidth
                                  variant='outlined'
                                  value={site.name}
                                  onChange={(e) =>
                                    handleUpdateSite(org.id, site.id, {
                                      name: e.target.value,
                                    })
                                  }
                                  sx={textfieldStyle}
                                />
                              </TableCell>
                              <TableCell align='center'>
                                <TextField
                                  type='text'
                                  size='small'
                                  sx={{ ...textfieldStyle, width: 120 }}
                                  value={site.accessPoints}
                                  onChange={(e) => {
                                    const val = e.target.value.replace(
                                      /\D/g,
                                      "",
                                    );
                                    if (val === "" || Number(val) <= 100000) {
                                      handleUpdateSite(org.id, site.id, {
                                        accessPoints: val,
                                      });
                                    }
                                  }}
                                />
                              </TableCell>
                              <TableCell align='center'>
                                <TextField
                                  type='text'
                                  size='small'
                                  sx={{ ...textfieldStyle, width: 120 }}
                                  value={site.switches}
                                  onChange={(e) => {
                                    const val = e.target.value.replace(
                                      /\D/g,
                                      "",
                                    );
                                    if (val === "" || Number(val) <= 100000) {
                                      handleUpdateSite(org.id, site.id, {
                                        switches: val,
                                      });
                                    }
                                  }}
                                />
                              </TableCell>
                              <TableCell align='center'>
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                  }}
                                >
                                  <CheckboxField
                                    checked={site.healthAi}
                                    onChange={(checked) =>
                                      handleUpdateSite(org.id, site.id, {
                                        healthAi: checked,
                                        nmsPro: checked,
                                      })
                                    }
                                  />
                                </Box>
                              </TableCell>
                              <TableCell align='right'>
                                <TrashIconButton
                                  onClick={() =>
                                    handleDeleteSite(org.id, site.id)
                                  }
                                  size='20px'
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Stack>
                </Box>
              </MainCard>
            ))}
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

export default UsageEmulator;
